/**
 * UI language: **English and German only** (owner decision).
 *
 * Scope is deliberately narrow — this switches the SHELL's own strings and `<html lang>`. Translating manual
 * *content* is a separate problem (the legacy `TextLoader` + the §-glossary + dual-format multilingual values) and
 * belongs to its own ADR.
 *
 * `<html lang>` is not cosmetic: screen readers pick pronunciation from it, so German text announced with an
 * English voice is the failure this prevents.
 */
import { TenantLocale } from "@arc/tenant-config";

export const LOCALE_STORAGE_KEY = "arc-locale";
/**
 * Derived from the tenant schema's own enum, not re-declared (ADR-047 D2). One list means a tenant can never
 * configure a `defaultLocale` the picker does not offer, nor the reverse — a drift that would surface as a single
 * tenant failing to parse, in production, at import time.
 */
export const LOCALES = TenantLocale.options;
export type Locale = TenantLocale;

/** Endonyms — a language picker names each language IN that language, never in the current one. */
export const LOCALE_LABEL: Record<Locale, string> = { en: "English", de: "Deutsch" };

/**
 * Flags, as a DECORATIVE aid only.
 *
 * ⚠ A flag is a country, not a language — German is spoken in Austria, Switzerland and beyond, and four of five
 * tenants here are Austrian. So the flag never carries the meaning: it is `aria-hidden`, and the endonym above
 * remains the label and the accessible name. It is a scanning aid for sighted users, nothing more.
 *
 * 🇬🇧 rather than 🇺🇸 for English, and 🇩🇪 for German, purely as the most widely recognised marks — no claim
 * about which variety of either language the UI uses.
 */
export const LOCALE_FLAG: Record<Locale, string> = { en: "🇬🇧", de: "🇩🇪" };

export function isLocale(v: unknown): v is Locale {
  return typeof v === "string" && (LOCALES as readonly string[]).includes(v);
}

/**
 * Normalize a BCP-47 tag to a supported locale. `de-AT`, `de-CH`, `de_DE` → `de`.
 *
 * Austrian and German tenants are the majority here, so treating `de-AT` as unsupported would send most users to
 * the wrong language on first visit.
 */
export function normalizeLocale(tag: string | undefined | null): Locale | null {
  const base = (tag ?? "").toLowerCase().replace("_", "-").split("-")[0];
  return isLocale(base) ? base : null;
}

/**
 * Resolution order: **stored choice → browser language → the TENANT's default → `en`** (ADR-047 D2).
 *
 * The tenant sits BELOW the browser, not above it. Above, two things break: an explicit English browser preference
 * would be overridden into German, and — because `defaultLocale` is required of every tenant — the browser step
 * would become unreachable. So the tenant default fires in exactly one case: a browser asking for a language the
 * shell does not ship. An Italian or Turkish browser on an Austrian tenant used to land on English; now it lands
 * on German, which is what the legacy did (`defaultLanguage = "de"`).
 *
 * `tenantDefault` is optional because `readBootstrap` deliberately tolerates its absence (a cached pre-ADR-047
 * document); omitting it reproduces the old behaviour rather than throwing.
 *
 * The Keycloak `locale` claim stays absent from the chain: the `profile` scope ships a mapper for it, but
 * `internationalizationEnabled` is false on every realm so nothing ever sets the attribute, and the verifier does
 * not extract it. Adding it here would be a step that can never fire.
 */
export function readLocale(
  tenantDefault?: Locale | null,
  storage: Pick<Storage, "getItem"> = safeStorage(),
  nav: Pick<Navigator, "language"> = typeof navigator === "undefined" ? { language: "en" } : navigator,
): Locale {
  try {
    const stored = storage.getItem(LOCALE_STORAGE_KEY);
    if (isLocale(stored)) return stored;
  } catch {
    /* storage can throw on access alone (Safari private mode) */
  }
  return normalizeLocale(nav.language) ?? tenantDefault ?? "en";
}

/**
 * Apply WITHOUT recording a choice. Sets `<html lang>` so assistive tech pronounces the page correctly.
 *
 * **The split from {@link selectLocale} is the other half of the ADR-047 D2 fix, and it is not cosmetic.** This
 * function used to persist unconditionally, and `main.tsx` calls it on every boot with the *auto-resolved* value —
 * so the very first page view wrote that resolution to `localStorage` as though the user had picked it. From then
 * on the stored value wins the chain forever, which means a fallback correction can never reach anyone who has
 * already opened the app. Only an actual selection may be recorded.
 */
export function applyLocale(locale: Locale, doc: Document = document): void {
  doc.documentElement.setAttribute("lang", locale);
}

/** An explicit choice: apply it AND remember it. The language picker is the only caller — see {@link applyLocale}. */
export function selectLocale(locale: Locale, doc: Document = document, storage = safeStorage()): void {
  applyLocale(locale, doc);
  try {
    storage.setItem(LOCALE_STORAGE_KEY, locale);
  } catch {
    /* storage disabled — the choice still applies for this session */
  }
}

function safeStorage(): Pick<Storage, "getItem" | "setItem"> {
  try {
    return window.localStorage;
  } catch {
    return { getItem: () => null, setItem: () => undefined };
  }
}
